/**
 * Utility to detect private browsing mode or restrictive privacy settings
 * Note: Detection is not 100% reliable, but attempts multiple methods
 * 
 * Returns a Promise that resolves to true if private browsing or restrictive privacy 
 * settings are detected, false otherwise. Also detects Safari iOS 18+ "Advanced Privacy 
 * Protections" that can cause similar issues.
 * 
 * Gracefully returns false if detection methods fail to avoid false positives.
 */
export async function detectPrivateBrowsing(): Promise<boolean> {
	if (typeof window === "undefined") {
		return false
	}

	try {
		// Method 1: Try to write to localStorage
		// In private mode or with strict privacy, setItem may succeed but the value won't persist
		const testKey = "__private_browsing_test__"
		try {
			localStorage.setItem(testKey, "test")
			const retrieved = localStorage.getItem(testKey)
			localStorage.removeItem(testKey)
			
			// If we can't retrieve what we set, likely private mode or restrictive settings
			if (retrieved !== "test") {
				return true
			}
		} catch {
			// If localStorage operations throw, likely private mode or restricted
			return true
		}
		
		// Method 2: Check indexedDB availability
		// In some private modes (especially Safari iOS), indexedDB is disabled
		if (typeof indexedDB === "undefined" || indexedDB === null) {
			return true
		}
		
		// Method 3: Test sessionStorage persistence
		// Similar to localStorage, sessionStorage may not persist in private mode
		try {
			const sessionTestKey = "__private_browsing_session_test__"
			sessionStorage.setItem(sessionTestKey, "test")
			const sessionRetrieved = sessionStorage.getItem(sessionTestKey)
			sessionStorage.removeItem(sessionTestKey)
			
			if (sessionRetrieved !== "test") {
				return true
			}
		} catch {
			return true
		}
		
		// Method 4: Check storage quota (iOS 18 Safari private/strict mode has very limited quota)
		// Private mode or advanced privacy protections typically grant only a few MB of storage
		if (navigator.storage && typeof navigator.storage.estimate === "function") {
			try {
				const estimate = await navigator.storage.estimate()
				// Private mode or strict privacy often has quota < 10MB (very limited)
				// Regular mode typically has quota > 50MB
				if (estimate.quota && estimate.quota < 10 * 1024 * 1024) {
					return true
				}
			} catch {
				// If estimate fails, continue with other methods
			}
		}
		
		// Method 5: Test cookie persistence
		// In private mode or with strict privacy, cookies may not persist
		try {
			const cookieTestKey = "__private_browsing_cookie_test__"
			document.cookie = `${cookieTestKey}=test; path=/`
			const cookieExists = document.cookie.indexOf(cookieTestKey) !== -1
			document.cookie = `${cookieTestKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
			
			// If cookie can't be set, might be private mode or restrictive settings
			if (!cookieExists) {
				return true
			}
		} catch {
			// Continue with other methods if cookie test fails
		}
		
		// If all checks pass, likely not private mode
		return false
	} catch {
		// If any error occurs during detection, gracefully return false
		// We prefer false negatives over false positives
		return false
	}
}

